import React, { useMemo, useState } from 'react';
import { NSButton } from 'bricks';
import { downloadFileFromUrl } from 'helpers/file/download';
import ReadOnlyWrapper from 'components/ReadOnlyWrapper/ReadOnlyWrapper';
import useToast, { ToastType } from 'hooks/useToast';
import { ProFormaModelIds } from 'constants/proForma';
import RenderIf from 'components/RenderIf/RenderIf';
import { isEmpty } from 'lodash';
import { isLandDevelopmentModel } from 'helpers/proForma';
import { useMapCoordinates } from 'hooks/useMapCoordinates';
import { useGetProFormaExecutiveSummary } from '../../hooks';
import { getProFormaExecutiveSummaryPdf } from '../../hooks/useGetProFormaExecutiveSummaryPdf';
import ExecutiveSummaryContent from './ExecutiveSummaryContent/ExecutiveSummaryContent';
import ExecutiveSummaryTimelineChart from './ExecutiveSummaryTimelineChart/ExecutiveSummaryTimelineChart';
import { useProFormaSocketContext } from '../ProForma/socketContext/ProFormaSocketProvider';
import { SUPPORTED_EXECUTIVE_SUMMARY_EXPORT_MODELS } from './constants';
import { buildBlocks } from './layout/buildBlocks';
import { legacyBlocks } from './legacyBlocks';
import { proFormaResolvers } from './resolvers/proForma';
import { ProFormaSummaryData } from './types';

interface IProps {
    hasWriteAccessToProForma: boolean;
    dealId: number;
    proFormaId: number;
}

const ExecutiveSummary: React.FC<IProps> = ({ hasWriteAccessToProForma, dealId, proFormaId }) => {
    const [isDownloading, setIsDownloading] = useState(false);
    const { showToast } = useToast();

    const {
        disposition, returnsData, uses, sources, proForma, waterfallAnalysis,
        incomeSummary, operatingInfo, permanentDebtsInfo, expenses, wattsType,
        reportMetrics, showCents, buildableLots, residentialLotMixes,
    } = useProFormaSocketContext();

    const isLandDev = isLandDevelopmentModel(proForma.proFormaModelId);
    const isApartment = proForma.proFormaModelId === ProFormaModelIds.APARTMENT;

    const summaryData: ProFormaSummaryData = useMemo(() => ({
        proForma,
        incomeSummary,
        buildableLots,
        operatingInfo,
        expenses,
        wattsType,
        uses,
        sources,
        disposition,
        permanentDebtInfo: permanentDebtsInfo?.[0],
        returnsData,
        waterfallAnalysis,
        residentialLotMixes,
        reportMetrics,
    }), [
        proForma, incomeSummary, buildableLots, operatingInfo, expenses, wattsType, uses, sources,
        disposition, permanentDebtsInfo, returnsData, waterfallAnalysis, residentialLotMixes, reportMetrics,
    ]);

    const blocks = useMemo(() => (isApartment
        ? buildBlocks({ proFormaModelId: proForma.proFormaModelId }, proFormaResolvers(summaryData))
        : legacyBlocks(summaryData)
    ), [isApartment, proForma.proFormaModelId, summaryData]);

    const { lat, lng } = useMapCoordinates(proForma ? {
        address: proForma.address ?? undefined,
        city: proForma.city ?? undefined,
        state: proForma.state ?? undefined,
        zipCode: proForma.zipCode ?? undefined,
        latitude: proForma.latitude ?? undefined,
        longitude: proForma.longitude ?? undefined,
        useCoordinates: Boolean(proForma.useCoordinates),
    } : null);

    const { data } = useGetProFormaExecutiveSummary(dealId, proFormaId, { enabled: !!proFormaId && !!dealId });

    const isExportSupported = SUPPORTED_EXECUTIVE_SUMMARY_EXPORT_MODELS.includes(proForma.proFormaModelId);

    const exportDealOverviewPdf = async () => {
        if (data) {
            setIsDownloading(true);
            const toastId = showToast('Downloading PDF...', ToastType.Success, { autoClose: false });
            try {
                const response = await getProFormaExecutiveSummaryPdf(dealId, proFormaId);
                const pdfUrl = response.url;
                await downloadFileFromUrl(pdfUrl, `${data.general.dealName}-executive-summary`, 'pdf', true);
                showToast(
                    'PDF downloaded successfully!',
                    ToastType.Success,
                    { autoClose: 3000 },
                    toastId,
                );
            } catch {
                showToast(
                    'Failed to download PDF',
                    ToastType.Error,
                    { autoClose: 3000 },
                    toastId,
                );
            } finally {
                setIsDownloading(false);
            }
        }
    };

    return (
        <RenderIf isTrue={!isEmpty(proForma)} isLoading={isEmpty(proForma)}>
            <ReadOnlyWrapper isReadOnly={!hasWriteAccessToProForma}>
                <ExecutiveSummaryContent
                    subtitle={proForma?.name}
                    blocks={blocks}
                    map={{ lat, lng }}
                    timeline={data && <ExecutiveSummaryTimelineChart data={[data.timelineData]} />}
                    headerAction={(
                        <NSButton
                            id="exec-summary-export-btn"
                            color="primary"
                            className="mr-2 heap-deal-pro-forma-exec-summary-export"
                            outline
                            text="Export"
                            callback={exportDealOverviewPdf}
                            disabled={isDownloading || !isExportSupported}
                            disabledTooltip={!isExportSupported ? 'Coming soon!' : ''}
                        />
                    )}
                    operationsTitle={isLandDev ? 'DISPOSITION SCHEDULE' : 'OPERATIONS'}
                    showCents={showCents}
                />
            </ReadOnlyWrapper>
        </RenderIf>
    );
};

export default ExecutiveSummary;
